import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { exhibitionColumnsVisibility } from "@/config/dashboard/exhibition";
import { sculptureEditionsColumnsVisibility } from "@/config/dashboard/sculpture-editions";
import { FormLayoutElement } from "@/config/forms/general";
import i18n, { SupportedLocale } from "@/i18n";
import { pickSculptureEditionTechniqueLine } from "@/lib/computeFields";
import {
  Exhibition,
  SculptureEdition,
  SculptureEditionInExhibition,
} from "@/lib/types";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkDrawerRowActions } from "@/components/table/LinkDrawerRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import SculptureEditionInExhibitionDrawer from "./drawer/SculptureEditionInExhibitionDrawer";
import { buildElColumns } from "./elColumnComposer";
import {
  ExhibitionLinkGridCard,
  SculptureEditionLinkGridCard,
} from "./relatedLinkGridCards";
import { GridCardWithActions } from "./GridCardWithActions";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";

interface ComplexLinkProps {
  id: string;
  exhibition?: Exhibition;
  sculptureEdition?: SculptureEdition;
  fields: FormLayoutElement[] | undefined;
  type: LinkableType;
}

type SculptureEditionToExhibitionLinksProps =
  LinksTableProps<SculptureEditionInExhibition> & {
    sculptureEdition?: SculptureEdition;
  };

type ExhibitionToSculptureEditionLinksProps =
  LinksTableProps<SculptureEditionInExhibition> & {
    exhibition?: Exhibition;
  };

/**
 * SculptureEditionInExhibitionsLinks component
 * @returns {JSX.Element}
 */
/** Colonnes quand on est sur une exposition : mêmes colonnes que MultipleLinks (œuvre → multiples). */
function renderSculptureEditionInExhibitionRowActionsWhenExhibition(
  data: SculptureEditionInExhibition,
  t: (key: string) => string,
  handleDeleteRelation: (id: string) => void,
  fields: FormLayoutElement[] | undefined,
  id: string,
  type: LinkableType,
) {
  if (!data.sculptureEdition) return null;
  return (
    <LinkDrawerRowActions
      previewType="sculptureEdition"
      previewId={data.sculptureEdition.id}
      previewElements={[
        {
          label: t("sculpture.form.sculptureEditionNumeroEnregistrement"),
          value: data.sculptureEdition.sculptureEditionNumeroEnregistrement,
          type: "text",
        },
        {
          label: t("sculpture.form.sculptureEditionNotes"),
          value: data.sculptureEdition.sculptureEditionNotes ?? "",
          type: "richtext",
        },
        {
          label: t("attachments"),
          value: data.sculptureEdition.attachments ?? [],
          type: "image",
        },
      ]}
      onDelete={() => handleDeleteRelation(data.id)}
      editDrawer={({ open, onOpenChange }) => (
        <SculptureEditionInExhibitionDrawer
          key={data.id}
          fields={fields}
          data={data}
          queryId={id}
          type={type}
          open={open}
          onOpenChange={onOpenChange}
        />
      )}
    />
  );
}

function renderSculptureEditionInExhibitionRowActionsWhenSculptureEdition(
  data: SculptureEditionInExhibition,
  t: (key: string) => string,
  handleDeleteRelation: (id: string) => void,
  fields: FormLayoutElement[] | undefined,
  id: string,
  type: LinkableType,
) {
  if (!data.exhibition) return null;
  return (
    <LinkDrawerRowActions
      previewType="exhibition"
      previewId={data.exhibition.id}
      previewElements={[
        {
          label: t("expositionsDescriptionNumeroExpo"),
          value: data.exhibition.expositionsDescriptionNumeroExpo,
          type: "text",
        },
        {
          label: t("expositionsDescriptionResume"),
          value: data.exhibition.expositionsDescriptionResume,
          type: "richtext",
        },
      ]}
      onDelete={() => handleDeleteRelation(data.id)}
      editDrawer={({ open, onOpenChange }) => (
        <SculptureEditionInExhibitionDrawer
          key={data.id}
          fields={fields}
          data={data}
          queryId={id}
          type={type}
          open={open}
          onOpenChange={onOpenChange}
        />
      )}
    />
  );
}

function columnsWhenExhibition(
  t: (key: string) => string,
  handleDeleteRelation: (id: string) => void,
  fields: FormLayoutElement[] | undefined,
  id: string,
  type: LinkableType,
): ColumnDef<SculptureEditionInExhibition, any>[] {
  const targetColumns: ColumnDef<SculptureEditionInExhibition, any>[] = [
    {
      accessorKey: "sculptureEditionNumeroEnregistrement",
      header: t("sculpture.form.sculptureEditionNumeroEnregistrement"),
      cell: ({ row }: { row: Row<SculptureEditionInExhibition> }) => (
        <div className="flex items-center gap-2 text-nowrap">
          <p className="text-[14px] font-[500]">
            {
              row.original.sculptureEdition
                ?.sculptureEditionNumeroEnregistrement
            }
          </p>
        </div>
      ),
    },
    {
      accessorKey: "sculptureEditionVignette",
      header: "",
      size: 100,
      cell: ({ row }) => (
        <ImageCell
          value=""
          attachments={row.original.sculptureEdition?.attachments ?? []}
          domain="sculptureEdition"
        />
      ),
    },
    {
      accessorKey: "sculptureEditionTechniquePreview",
      header: t("sculpture.form.sculptureEditionTechnique"),
      size: 200,
      cell: ({ row }) => {
        const se = row.original.sculptureEdition;
        if (!se) return null;
        return (
          <TextCell
            value={pickSculptureEditionTechniqueLine(se, {
              language: i18n.language as SupportedLocale,
            })}
          />
        );
      },
    },
    {
      accessorKey: "sculptureEditionDateRealisation",
      header: t("sculpture.form.sculptureEditionDateRealisation"),
      size: 200,
      cell: ({ row }) => (
        <TextCell
          value={
            row.original.sculptureEdition?.sculptureEditionDateRealisation ?? ""
          }
        />
      ),
    },
    {
      accessorKey: "sculptureEditionNotes",
      header: t("sculpture.form.sculptureEditionNotes"),
      size: 300,
      cell: ({ row }) => (
        <HtmlCell
          value={row.original.sculptureEdition?.sculptureEditionNotes ?? ""}
        />
      ),
    },
  ];

  const transitionColumns: ColumnDef<SculptureEditionInExhibition, any>[] = [
    {
      accessorKey: "sculptureEditionDansExpositionsNumeroCatalogue",
      header: t("expositionsDansEstampesMultiplesDescriptionNumero"),
      cell: ({ row }) => (
        <TextCell
          value={
            row.original.sculptureEditionDansExpositionsNumeroCatalogue ?? ""
          }
        />
      ),
    },
    {
      accessorKey: "sculptureEditionDansExpositionsNotes",
      header: t("expositionsDansEstampesMultiplesDescriptionExemplaireExpose"),
      cell: ({ row }) => (
        <HtmlCell
          value={row.original.sculptureEditionDansExpositionsNotes ?? ""}
        />
      ),
    },
  ];

  const actionsColumn = createActionsColumnDef<SculptureEditionInExhibition>(
    ({ row }: { row: Row<SculptureEditionInExhibition> }) =>
      renderSculptureEditionInExhibitionRowActionsWhenExhibition(
        row.original,
        t,
        handleDeleteRelation,
        fields,
        id,
        type,
      ),
  );
  return buildElColumns(targetColumns, transitionColumns, actionsColumn);
}

/** Colonnes quand on est sur une édition de sculpture : afficher les expositions liées. */
function columnsWhenSculptureEdition(
  t: (key: string) => string,
  handleDeleteRelation: (id: string) => void,
  fields: FormLayoutElement[] | undefined,
  id: string,
  type: LinkableType,
): ColumnDef<SculptureEditionInExhibition, any>[] {
  const targetColumns: ColumnDef<SculptureEditionInExhibition, any>[] = [
    {
      accessorKey: "exhibition.expositionsDescriptionNumeroExpo",
      header: t("expositionsDescriptionNumeroExpo"),
      cell: ({ row }: { row: Row<SculptureEditionInExhibition> }) => (
        <TextCell
          value={row.original.exhibition?.expositionsDescriptionNumeroExpo}
        />
      ),
    },
    {
      accessorKey: "exhibition.expositionsDescriptionVignette",
      header: "",
      size: 100,
      cell: ({ row }) => (
        <ImageCell
          value=""
          attachments={row.original.exhibition?.attachments ?? []}
          domain="exhibition"
        />
      ),
    },
    {
      accessorKey: "exhibition.expositionsDescriptionAnnee",
      header: t("expositionsDescriptionAnnee"),
      size: 10,
      cell: ({ row }) => (
        <TextCell
          value={row.original.exhibition?.expositionsDescriptionAnnee}
        />
      ),
    },
    {
      accessorKey: "exhibition.expositionsDescriptionTitre",
      header: t("expositionsDescriptionTitre"),
      size: 300,
      cell: ({ row }) => (
        <HtmlCell
          value={row.original.exhibition?.expositionsDescriptionTitre ?? ""}
        />
      ),
    },
    {
      accessorKey: "exhibition.expositionsDescriptionDates",
      header: t("expositionsDescriptionDates"),
      cell: ({ row }) => (
        <TextCell
          value={row.original.exhibition?.expositionsDescriptionDates}
        />
      ),
    },
    {
      accessorKey: "exhibition.expositionsDescriptionLieu",
      header: t("expositionsDescriptionLieu"),
      size: 300,
      cell: ({ row }) => (
        <div className="flex flex-col items-start text-wrap">
          <p className="text-[14px] font-[500]">
            {row.original.exhibition?.expositionsDescriptionVille}
          </p>
          <p className="text-[14px] font-[400] text-gray-500">
            {row.original.exhibition?.expositionsDescriptionLieu}
          </p>
        </div>
      ),
    },
    {
      accessorKey: "exhibition.expositionsDescriptionVille",
      header: t("expositionsDescriptionVille"),
      cell: ({ row }) => (
        <TextCell
          value={row.original.exhibition?.expositionsDescriptionVille}
        />
      ),
    },
    {
      accessorKey: "exhibition.expositionsDescriptionPays",
      header: t("expositionsDescriptionPays"),
      cell: ({ row }) => (
        <TextCell value={row.original.exhibition?.expositionsDescriptionPays} />
      ),
    },
    {
      accessorKey: "exhibition.expositionsPublicationStatus",
      header: t("expositionsPublicationStatus"),
      cell: ({ row }) => (
        <TextCell
          value={row.original.exhibition?.expositionsPublicationStatus}
        />
      ),
    },
  ];

  const transitionColumns: ColumnDef<SculptureEditionInExhibition, any>[] = [
    {
      accessorKey: "sculptureEditionDansExpositionsNumeroCatalogue",
      header: t("expositionsDansEstampesMultiplesDescriptionNumero"),
      cell: ({ row }: { row: Row<SculptureEditionInExhibition> }) => (
        <TextCell
          value={
            row.original.sculptureEditionDansExpositionsNumeroCatalogue ?? ""
          }
        />
      ),
    },
    {
      accessorKey: "sculptureEditionDansExpositionsNotes",
      header: t("expositionsDansEstampesMultiplesDescriptionExemplaireExpose"),
      cell: ({ row }: { row: Row<SculptureEditionInExhibition> }) => (
        <HtmlCell
          value={row.original.sculptureEditionDansExpositionsNotes ?? ""}
        />
      ),
    },
  ];

  const actionsColumn = createActionsColumnDef<SculptureEditionInExhibition>(
    ({ row }: { row: Row<SculptureEditionInExhibition> }) =>
      renderSculptureEditionInExhibitionRowActionsWhenSculptureEdition(
        row.original,
        t,
        handleDeleteRelation,
        fields,
        id,
        type,
      ),
  );
  return buildElColumns(targetColumns, transitionColumns, actionsColumn);
}

const SculptureEditionInExhibitionsLinks = ({
  id,
  exhibition,
  sculptureEdition,
  fields,
  type,
}: ComplexLinkProps) => {
  const { t } = useTranslation();
  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "sculptureEditionInExhibitions",
    type,
  );

  const { mutate: deleteSculptureEditionInExhibition } = useDeleteComplexLink(
    id,
    "sculptureEditionInExhibition",
    type,
  );

  const [searchContent, setSearchContent] = useState<string>("");

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    (relationId: string) => {
      deleteSculptureEditionInExhibition({
        id: relationId,
        relation: "sculptureEditionInExhibition",
      });
    },
    [deleteSculptureEditionInExhibition],
  );

  const renderSculptureEditionInExhibitionRowActions = useCallback(
    (data: SculptureEditionInExhibition) =>
      type === "exhibition"
        ? renderSculptureEditionInExhibitionRowActionsWhenExhibition(
            data,
            t,
            handleDeleteRelation,
            fields,
            id,
            type,
          )
        : renderSculptureEditionInExhibitionRowActionsWhenSculptureEdition(
            data,
            t,
            handleDeleteRelation,
            fields,
            id,
            type,
          ),
    [fields, handleDeleteRelation, id, t, type],
  );

  const columns = useMemo(
    () =>
      type === "exhibition"
        ? columnsWhenExhibition(t, handleDeleteRelation, fields, id, type)
        : columnsWhenSculptureEdition(
            t,
            handleDeleteRelation,
            fields,
            id,
            type,
          ),
    [fields, handleDeleteRelation, id, t, type],
  );

  return (
    <div className="w-full">
      {type === "exhibition" ? (
        <ExhibitionToSculptureEditionLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          exhibition={exhibition}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderSculptureEditionInExhibitionRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("linkedSculptureEditions")}
          layoutBackLabel={t("relatedRecords")}
          setIsAddMode={setIsAddMode}
        />
      ) : (
        <SculptureEditionToExhibitionLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          sculptureEdition={sculptureEdition}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderSculptureEditionInExhibitionRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("linkedExhibitions")}
          layoutBackLabel={t("relatedRecords")}
          setIsAddMode={setIsAddMode}
        />
      )}
    </div>
  );
};

/** Vue exposition : liste des multiples liés à cette exposition ; en mode ajout, multiples pas encore liés. */
const ExhibitionToSculptureEditionLinks = ({
  id,
  relatedData,
  fields,
  exhibition,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: ExhibitionToSculptureEditionLinksProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const STORAGE_KEY_ADD = "sculpture-edition-exhibition-add";
  const STORAGE_KEY_LINKED = "sculpture-edition-exhibition-linked";

  const defaultVisibilityLinked: VisibilityState = {
    sculptureEditionNumeroEnregistrement: true,
    sculptureEditionVignette: true,
    sculptureEditionTechniquePreview: true,
    sculptureEditionDateRealisation: true,
    sculptureEditionDansExpositionsNumeroCatalogue: true,
    sculptureEditionDansExpositionsNotes: true,
    actions: true,
  };

  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...sculptureEditionsColumnsVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...sculptureEditionsColumnsVisibility };
    });

  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityLinked, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityLinked };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }));
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const { data: notAlreadyRelatedEditions } =
    useGetNotRelatedResource<SculptureEdition>({
      resultType: "sculptureEdition",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    });

  const columnsEditions: ColumnDef<SculptureEdition, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "sculptureEditionNumeroEnregistrement",
        header: t("sculpture.form.sculptureEditionNumeroEnregistrement"),
        cell: ({ row }) => (
          <div className="flex items-center gap-2 text-nowrap">
            <p className="text-[14px] font-[500]">
              {row.original.sculptureEditionNumeroEnregistrement}
            </p>
          </div>
        ),
      },
      {
        accessorKey: "sculptureEditionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => (
          <ImageCell
            value=""
            attachments={row.original.attachments ?? []}
            domain="sculptureEdition"
          />
        ),
      },
      {
        accessorKey: "sculptureEditionTechniquePreview",
        header: t("sculpture.form.sculptureEditionTechnique"),
        size: 200,
        cell: ({ row }) => (
          <TextCell
            value={pickSculptureEditionTechniqueLine(row.original, {
              language: i18n.language as SupportedLocale,
            })}
          />
        ),
      },
      {
        accessorKey: "sculptureEditionDateRealisation",
        header: t("sculpture.form.sculptureEditionDateRealisation"),
        size: 200,
        cell: ({ row }) => (
          <TextCell
            value={row.original.sculptureEditionDateRealisation ?? ""}
          />
        ),
      },
      createActionsColumnDef<SculptureEdition>(({ row }) => {
          if (!exhibition) return null;
          return (
            <LinkDrawerRowActions
              previewType="sculptureEdition"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("sculpture.form.sculptureEditionNumeroEnregistrement"),
                  value: row.original.sculptureEditionNumeroEnregistrement ?? "",
                  type: "text",
                },
                {
                  label: t("sculpture.form.sculptureEditionNotes"),
                  value: row.original.sculptureEditionNotes ?? "",
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: row.original.attachments ?? [],
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <SculptureEditionInExhibitionDrawer
                  key={row.original.id}
                  fields={fields}
                  queryId={id}
                  type="exhibition"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    exhibition,
                    sculptureEdition: row.original,
                    sculptureEditionDansExpositionsNumeroCatalogue: "",
                    sculptureEditionDansExpositionsNotes: "",
                  }}
                />
              )}
            />
          );
        }),
    ];
  }, [exhibition, fields, id, t]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: relatedData?.length ?? 0,
  };

  const addModeTable = (
    <DataTable
      columns={columnsEditions}
      data={notAlreadyRelatedEditions?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notAlreadyRelatedEditions?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY_ADD}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityAdd,
        onColumnOrderChange: setColumnsOrderAdd,
        state: {
          columnVisibility: columnVisibilityAdd,
          columnOrder: columnsOrderAdd,
        },
      }}
    />
  );
  const listTable = (
    <DataTable
      columns={columnsLink}
      data={paginatedRelatedData}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY_LINKED}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityLinked,
        onColumnOrderChange: setColumnsOrderLinked,
        state: {
          columnVisibility: columnVisibilityLinked,
          columnOrder: columnsOrderLinked,
        },
      }}
    />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <SculptureEditionLinkGridCard
                    item={row.sculptureEdition}
                    t={t}
                    onTextClick={() => setSelectedRelationId(row.id)}
                  />
                </GridCardWithActions>
                <SculptureEditionInExhibitionDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="exhibition"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open: boolean) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

const SculptureEditionToExhibitionLinks = ({
  id,
  relatedData,
  fields,
  sculptureEdition,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: SculptureEditionToExhibitionLinksProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const [relatedPagination, setRelatedPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }));
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD_EXH = "sculpture-edition-links-exhibition-add";
  const STORAGE_KEY_LINKED_EXH = "sculpture-edition-links-exhibition-linked";
  const defaultVisibilityExh = {
    ...exhibitionColumnsVisibility,
    actions: true,
  };
  const [columnVisibilityAddExh, setColumnVisibilityAddExh] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD_EXH);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityExh, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityExh };
    });
  const [columnVisibilityLinkedExh, setColumnVisibilityLinkedExh] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_EXH);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityExh, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityExh };
    });
  const [columnsOrderAddExh, setColumnsOrderAddExh] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD_EXH);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinkedExh, setColumnsOrderLinkedExh] = useState<string[]>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_EXH);
        if (saved) {
          const { order } = saved;
          return order || [];
        }
      } catch {
        // ignore
      }
      return [];
    },
  );

  const { data: notAlreadyRelatedExhibitions } =
    useGetNotRelatedResource<Exhibition>({
      resultType: "exhibition",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    });

  const columnsExhibitions: ColumnDef<Exhibition, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "expositionsDescriptionNumeroExpo",
        header: t("expositionsDescriptionNumeroExpo"),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <TextCell value={row.original.expositionsDescriptionNumeroExpo} />
          );
        },
      },
      {
        accessorKey: "expositionsDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            row.original.attachments && (
              <ImageCell
                value={"expositionsDescriptionVignette"}
                attachments={row.original.attachments}
                domain={"exhibition"}
              />
            )
          );
        },
      },
      {
        accessorKey: "expositionsDescriptionAnnee",
        header: t("expositionsDescriptionAnnee"),
        size: 10,
        cell: ({ row }) => {
          return <TextCell value={row.original.expositionsDescriptionAnnee} />;
        },
      },
      {
        accessorKey: "expositionsDescriptionTitre",
        header: t("expositionsDescriptionTitre"),
        size: 300,
        cell: ({ row }) => {
          return <HtmlCell value={row.original.expositionsDescriptionTitre} />;
        },
      },
      {
        accessorKey: "expositionsDescriptionDates",
        header: t("expositionsDescriptionDates"),
        cell: ({ row }) => {
          return <TextCell value={row.original.expositionsDescriptionDates} />;
        },
      },
      {
        accessorKey: "expositionsDescriptionLieu",
        header: t("expositionsDescriptionLieu"),
        size: 300,
        cell: ({ row }) => {
          return (
            <div className="flex flex-col items-start text-wrap">
              <p className="text-[14px] font-[500]">
                {row.original.expositionsDescriptionVille}
              </p>
              <p className="text-[14px] font-[400] text-gray-500">
                {row.original.expositionsDescriptionLieu}
              </p>
            </div>
          );
        },
      },
      createActionsColumnDef<Exhibition>(({ row }) => {
          const data = row.original as Exhibition;
          if (!sculptureEdition) return null;
          return (
            <LinkDrawerRowActions
              previewType="exhibition"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("expositionsDescriptionNumeroExpo"),
                  value: data.expositionsDescriptionNumeroExpo,
                  type: "text",
                },
                {
                  label: t("attachments"),
                  value: data.attachments,
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <SculptureEditionInExhibitionDrawer
                  key={row.original.id}
                  fields={fields}
                  queryId={id}
                  type="sculptureEdition"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    exhibition: data,
                    sculptureEdition: sculptureEdition!,
                    sculptureEditionDansExpositionsNumeroCatalogue: "",
                    sculptureEditionDansExpositionsNotes: "",
                  }}
                />
              )}
            />
          );
        }),
    ];
  }, [fields, id, sculptureEdition, t]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: relatedData?.length ?? 0,
  };
  const addModeTable = (
    <DataTable
      columns={columnsExhibitions}
      data={notAlreadyRelatedExhibitions?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notAlreadyRelatedExhibitions?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY_ADD_EXH}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityAddExh,
        onColumnOrderChange: setColumnsOrderAddExh,
        state: {
          columnVisibility: columnVisibilityAddExh,
          columnOrder: columnsOrderAddExh,
        },
      }}
    />
  );
  const listTable = (
    <DataTable
      columns={columnsLink}
      data={paginatedRelatedData}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY_LINKED_EXH}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityLinkedExh,
        onColumnOrderChange: setColumnsOrderLinkedExh,
        state: {
          columnVisibility: columnVisibilityLinkedExh,
          columnOrder: columnsOrderLinkedExh,
        },
      }}
    />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED_EXH}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <ExhibitionLinkGridCard
                    item={row.exhibition}
                    t={t}
                    onTextClick={() => setSelectedRelationId(row.id)}
                  />
                </GridCardWithActions>
                <SculptureEditionInExhibitionDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="sculptureEdition"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open: boolean) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

const SculptureEditionInExhibitions = SculptureEditionInExhibitionsLinks;
export default SculptureEditionInExhibitions;
